/**
 * plugins/index.js
 *
 * Included in `./src/main.js`
 */

// Plugins
import { createVuetify } from "./vuetify";
import { createI18n } from "./i18n";
import { createRouter } from "../router";
import { createStores } from "../stores";
import { useSettingsStore } from "../stores/settingsStore";
import { createOfetch } from "./ofetch";
import { createSentry } from "./sentry";
import { createValidator } from "./validator";
import { createMatomo } from "./matomo";
import { createStaleBuildGuard } from "./staleBuild";
import VuetifyUseDialog from "@felicienfrancois/vuetify-use-dialog";
import { createPhoneInput } from "./phone-input";

/**
 *
 * @param {import("vue").App<Element>} app
 */
export async function registerPlugins(app) {
  const stores = createStores();
  app.use(stores);
  await useSettingsStore().loadSettings();

  const i18n = await createI18n();
  app.use(i18n);

  const authStore = useAuthStore();
  await authStore.authenticate();
  useStepsStore().init(i18n, authStore);

  const validator = await createValidator(i18n);
  app.use(validator);

  const ofetch = createOfetch(i18n);
  app.use(ofetch);

  const router = createRouter();
  app.use(router);

  const vuetify = createVuetify(i18n);
  app.use(vuetify);

  app.use(VuetifyUseDialog, {
    confirmDialog: {
      dialogProps: {
        maxWidth: 560,
      },
      confirmationButtonProps: {
        color: "secondary",
      },
      cancellationButtonProps: {
        variant: "text",
        color: "grey-darken-2",
      },
      cardActionsProps: {
        class: "justify-center",
      },
      cardTitleProps: {
        class: "bg-grey-lighten-3",
      },
      cardTextProps: {
        class: "pt-6 text-pre-line",
      },
    },
  });

  const phoneInput = createPhoneInput(i18n);
  app.use(phoneInput);

  if (import.meta.env.PROD) {
    // Web build only (mode production): native builds (android, ios, browser) bundle their files and would poll the web deployment.
    if (import.meta.env.MODE === "production") {
      const staleBuildGuard = createStaleBuildGuard(router);
      app.use(staleBuildGuard);
    }

    const matomo = createMatomo(app, router);
    app.use(matomo);

    const sentry = createSentry(router);
    app.use(sentry);
  }

  // Handle device ready (splashscreen)
  document.addEventListener("deviceready", () => {
    if (navigator.splashscreen) navigator.splashscreen.hide();
  });

  return app;
}
